import React from 'react';
import { flushSync } from 'react-dom';
import { Box, Typography } from '@mui/material';
import { Add, NotificationsOutlined } from '@mui/icons-material';
import { useTheme } from '@emotion/react';
import { DragDropContext } from 'react-beautiful-dnd';
import { useToast } from 'context/ToastContext';
import { FetchKanbanBoard, FetchKanbanColumnCards, MoveKanbanCard, UndoKanbanMove, PlaceKanbanCards } from 'api/kanban';
import KanbanColumn, { CardSkeleton, shimmer } from './KanbanColumn';
import ReelModal from 'ui-components/ReelModal';
import { useSelector } from 'react-redux';
import { clearColumn, columnCheck, hasSelection, isCardSelected, selectCards, selectColumn, selectedCount, toggleCard } from '../selection';

const PAGE_LIMIT = 10;
const REFRESH_LIMIT = 50; // most cards per column an in-place refresh refetches
const SKELETON_CARD_CAP = 8; // never render more skeletons than fill the viewport
const SKELETON_DEFAULT = 4; // per-column count when we have no prior count (cold load)

// How close (px) to the board's bottom edge the pointer must be to reveal the horizontal bar.
const H_SCROLL_ZONE = 48;
// Neutral lane tint while the board loads (status colours arrive with the data).
const SKELETON_TINT = 'rgba(135, 144, 158, 0.1)';

// sendCustomizedEmails branches on `rejected` and `in-progress` only - there is no
// else, so moving to New (invited) or Shortlisted (interested) never mails.
const EMAILING_STATUSES = ['in-progress', 'rejected'];

// A column-shaped skeleton: header (label if known, else a shimmer bar) + N card
// skeletons, N based on the column's last-known count (ClickUp-style first load).
// `reels[i]` says whether card i had a video pitch, so the reel button lines up.
const ColumnSkeleton = ({ label, cardCount, reels = [], collapsed = false }) => {
    if (collapsed) {
        // Same footprint as a collapsed strip, so nothing jumps when the board loads.
        return <Box sx={{ width: 56, flexShrink: 0, minHeight: 200, backgroundColor: SKELETON_TINT, borderRadius: '12px' }} />;
    }
    const n = Math.max(1, Math.min(SKELETON_CARD_CAP, cardCount || SKELETON_DEFAULT));
    return (
        <Box sx={{ width: { xs: 320, sm: 384 }, flexShrink: 0, backgroundColor: SKELETON_TINT, borderRadius: '12px 12px 8px 8px', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            <Box sx={{ p: '8px 8px 8px calc(1.6rem + 4px)', height: 40, display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                {label
                    ? <Typography sx={{ fontWeight: 600, fontSize: '0.75rem', textTransform: 'uppercase', color: '#475467' }}>{label}</Typography>
                    : <Box sx={{ ...shimmer, width: 100, height: 20, borderRadius: '6px' }} />}
                <Box sx={{ ...shimmer, width: 16, height: 12, borderRadius: '4px' }} />
            </Box>
            <Box sx={{ p: '0 0.5rem 0.5rem' }}>
                {Array.from({ length: n }).map((_, i) => <CardSkeleton key={i} withReel={!!reels[i]} />)}
            </Box>
        </Box>
    );
};

const findColumn = (cols, id) => cols.find((c) => String(c._id) === String(id));
const findCardColumn = (cols, pipelineId) => (cols || []).find((c) => c.cards.some((x) => String(x.pipeline_id) === String(pipelineId)));
const countLoadedSelected = (cols, selection) => (cols || []).reduce((n, c) => n + c.cards.filter((x) => isCardSelected(selection, x)).length, 0);
// Moves one card (by pipeline id) into a column at an index, keeping counts right. Used to
// put cards back on Undo without disturbing anything else that changed meanwhile.
const moveCardLocal = (cols, pipelineId, toColumnId, index) => {
    const from = findCardColumn(cols, pipelineId);
    if (!from || String(from._id) === String(toColumnId)) return cols;
    return cols.map((c) => {
        if (c === from) {
            return { ...c, cards: c.cards.filter((x) => String(x.pipeline_id) !== String(pipelineId)), count: Math.max(0, (c.count || 1) - 1) };
        }
        if (String(c._id) === String(toColumnId)) {
            const card = from.cards.find((x) => String(x.pipeline_id) === String(pipelineId));
            const cards = [...c.cards];
            cards.splice(Math.min(index, cards.length), 0, { ...card, investor_status_id: c._id });
            return { ...c, cards, count: (c.count || 0) + 1 };
        }
        return c;
    });
};

const UNDO_MS = 10000; // matches the backend's email hold

const plural = (n) => `${n} startup${n === 1 ? '' : 's'}`;
// A modal or menu is open; Esc belongs to it, not to the board selection.
const isOverlayOpen = () => !!document.querySelector('.MuiModal-root');

// Multi-drag drop (Atlassian multi-drag pattern): dragged card first, the rest in board
// order, all inserted at the drop index. `moved` lists only cards that changed column.
const multiDrop = (columns, draggableId, destination, isSel) => {
    const next = columns.map((c) => ({ ...c, cards: [...c.cards] }));
    const to = findColumn(next, destination.droppableId);
    if (!to) return null;

    const picked = [];
    next.forEach((c) => c.cards.forEach((card, index) => {
        if (isSel(card)) picked.push({ card, from: c, index });
    }));
    const pickedCards = new Set(picked.map((p) => p.card));
    const dragged = picked.find((p) => String(p.card.pipeline_id) === String(draggableId));
    if (!dragged) return null;
    const ordered = [dragged, ...picked.filter((p) => p !== dragged)];

    // The other selected cards are hidden during a group drag, so destination.index already
    // counts only the cards that stay - it is the insert point once all selected are removed.
    const insertAt = Math.max(0, destination.index);

    next.forEach((c) => {
        const before = c.cards.length;
        c.cards = c.cards.filter((card) => !pickedCards.has(card));
        const removed = before - c.cards.length;
        if (removed && c !== to) c.count = Math.max(0, (c.count || removed) - removed);
    });
    const moved = ordered.filter((p) => p.from !== to);
    to.count = (to.count || 0) + moved.length;
    to.cards.splice(insertAt, 0, ...ordered.map((p) => ({ ...p.card, investor_status_id: to._id })));

    return { next, to, moved, insertAt, ids: ordered.map((p) => p.card.pipeline_id) };
};

// Owns the board state (single source of truth for drag-drop) and persists cross-column
// moves as silent status changes with optimistic UI + rollback. Search / filters /
// selection / reload are controlled by the parent page.
const KanbanBoard = ({ investor_id, pipelineId, search = '', filterParams = {}, selection = {}, onSelectionChange, onClearSelection, reloadKey = 0, onAddStatus, onOpenCardMenu, onCardAction, onManageStatus, onInviteStartup, statuses = [], customizeEmail, onColumnsChange }) => {
    const theme = useTheme();
    const indigo = theme?.palette?.customColors?.indigo600 || '#444CE7';
    const [showHScroll, setShowHScroll] = React.useState(false);
    const { addToast } = useToast();
    const userPermissions = useSelector((state) => state?.permissions?.userPermissions);
    const { mutateAsync: fetchBoardAsync } = FetchKanbanBoard();
    const { mutateAsync: fetchCardsAsync } = FetchKanbanColumnCards();
    const { mutateAsync: moveCardAsync } = MoveKanbanCard();
    const { mutateAsync: undoMoveAsync } = UndoKanbanMove();
    const { mutateAsync: placeCardsAsync } = PlaceKanbanCards();

    const [columns, setColumns] = React.useState(null);
    // Columns currently fetching a page (multiple can auto-load at once).
    const [loadingIds, setLoadingIds] = React.useState(() => new Set());
    const [isDragging, setIsDragging] = React.useState(false);
    const [reelModalOpen, setReelModalOpen] = React.useState(false);
    const [selectedReelItem, setSelectedReelItem] = React.useState(null);
    const [selectedReelViewType, setSelectedReelViewType] = React.useState('deal-flow');
    // Guards against duplicate / concurrent page loads for the same column.
    const inFlightRef = React.useRef(new Set());
    // Multi-drag: { pipelineId, count } while a card is lifted; count > 1 means a group drag.
    const [dragInfo, setDragInfo] = React.useState(null);
    // Last card clicked or ticked, the start point for a Shift+click range.
    const anchorRef = React.useRef(null);
    const columnsRef = React.useRef(columns);
    columnsRef.current = columns;

    // Collapsed columns (status ids), remembered per investor in this browser - a personal
    // view choice, so not a shared server setting.
    const collapseKey = `kb_collapsed:${investor_id}`;
    const [collapsedIds, setCollapsedIds] = React.useState(() => {
        try { return new Set(JSON.parse(localStorage.getItem(collapseKey) || '[]')); } catch (e) { return new Set(); }
    });

    // Cache the last successful layout ({_id,label,count}) per pipeline tab in
    // localStorage, so the loading skeleton (on reload / re-open / tab change) shows
    // each column's REAL card count — not a generic default.
    const structKey = React.useCallback((pid) => `kb_struct:${investor_id}:${pid}`, [investor_id]);
    const readStruct = React.useCallback((pid) => {
        try {
            const raw = localStorage.getItem(structKey(pid));
            return raw ? JSON.parse(raw) : null;
        } catch (e) {
            return null;
        }
    }, [structKey]);
    const writeStruct = React.useCallback((pid, cols) => {
        try {
            localStorage.setItem(structKey(pid), JSON.stringify(cols.map((c) => ({
                _id: c._id,
                label: c.label,
                count: c.count,
                // Which of the first cards carried a video pitch — so the skeleton shows the
                // reel button on exactly the same cards the real board will.
                reels: (c.cards || []).slice(0, SKELETON_CARD_CAP).map((x) => !!x?.video_pitch),
            }))));
        } catch (e) { /* ignore quota / private-mode errors */ }
    }, [structKey]);

    // Stable string of the current filter set so effects re-run when it changes.
    const filterKey = JSON.stringify(filterParams || {});

    // Surface the per-column counts the board already holds, so the page above does
    // not have to re-fetch them. Fires on load, on drags and on pagination.
    React.useEffect(() => {
        if (columns) onColumnsChange?.(columns);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [columns]);

    // Bumped on every board load; a load-more answer that belongs to an older board is dropped.
    const boardGenRef = React.useRef(0);
    // The view (tab + search + filters) on screen. Reloading the same view refreshes in place,
    // like the list view: the board stays visible, no skeleton.
    const viewKey = `${investor_id}|${pipelineId}|${search}|${filterKey}`;
    const loadedViewRef = React.useRef(null);

    const loadBoard = React.useCallback(() => {
        if (!investor_id) return;
        const gen = ++boardGenRef.current;
        const current = columnsRef.current;
        const silent = !!current && loadedViewRef.current === viewKey;
        // Refetch as deep as the investor has scrolled (capped), so the refresh doesn't jump.
        const deepest = silent ? Math.max(0, ...current.map((c) => c.cards.length)) : 0;
        if (!silent) setColumns(null);
        fetchBoardAsync(
            { investor_id, investor_pipeline_id: pipelineId, limit: Math.min(REFRESH_LIMIT, Math.max(PAGE_LIMIT, deepest)), name: search, ...filterParams },
            {
                onSuccess: (res) => {
                    if (gen !== boardGenRef.current) return;
                    const cols = res?.data?.data?.columns || [];
                    writeStruct(pipelineId, cols);
                    loadedViewRef.current = viewKey;
                    setColumns(cols);
                },
                onError: () => {
                    if (gen !== boardGenRef.current) return;
                    if (silent) addToast('Could not refresh the board.', 'error');
                    else setColumns([]);
                },
            }
        );
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [investor_id, pipelineId, search, filterKey, reloadKey, fetchBoardAsync]);

    React.useEffect(() => {
        loadBoard();
    }, [loadBoard]);

    // Follow Manage Status in place (like the list view's tabs), keeping loaded cards: reorder,
    // add a new status as an empty column, drop a deleted one (the API only deletes empty statuses).
    React.useEffect(() => {
        if (!statuses?.length) return;
        const idOf = (s) => String(s?._id ?? s?.value);
        const rank = new Map(statuses.map((s, i) => [idOf(s), i]));
        setColumns((prev) => {
            if (!prev) return prev;
            const have = new Set(prev.map((c) => String(c._id)));
            const added = statuses
                .filter((s) => !have.has(idOf(s)))
                .map((s) => ({ _id: s?._id ?? s?.value, label: s?.label, value: s?.mappedValue, display_order: s?.display_order, count: 0, cards: [] }));
            const kept = prev.filter((c) => rank.has(String(c._id)) || (c.count || 0) > 0);
            const next = [...kept, ...added].sort((a, b) => (rank.get(String(a._id)) ?? Infinity) - (rank.get(String(b._id)) ?? Infinity));
            if (next.length === prev.length && next.every((col, i) => col === prev[i])) return prev;
            writeStruct(pipelineId, next);
            return next;
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [statuses]);

    // PitchReelButton expects statuses shaped { status: label, value: _id }
    const reelStatuses = React.useMemo(
        () => (columns || []).map((c) => ({ status: c.label, value: c._id })),
        [columns]
    );

    // The board API's columns carry no colour or icon, so join them in from the statuses list
    // (kept current by Manage Status). Without this the column pills ignore both.
    const styledColumns = React.useMemo(() => {
        const meta = new Map((statuses || []).map((s) => [String(s?._id ?? s?.value), s]));
        return (columns || []).map((c) => {
            const m = meta.get(String(c._id));
            // The board API's `value` is the fixed status key (e.g. 'interested'); it drives the default icon.
            // The name comes from the statuses list too, so admin defaults and investor renames both show.
            return { ...c, label: m?.label ?? c.label, color: m?.color, icon: m?.icon, mappedValue: m?.mappedValue ?? c.value };
        });
    }, [columns, statuses]);

    const handleLoadMore = async (column) => {
        if (inFlightRef.current.has(column._id)) return; // already fetching this column
        inFlightRef.current.add(column._id);
        setLoadingIds((s) => new Set(s).add(column._id));
        const gen = boardGenRef.current;
        try {
            const res = await fetchCardsAsync({
                investor_id,
                investor_status_id: column._id,
                investor_pipeline_id: pipelineId,
                offset: column.cards.length,
                limit: PAGE_LIMIT,
                name: search,
                ...filterParams,
            });
            if (gen !== boardGenRef.current) return; // the board reloaded meanwhile
            const more = res?.data?.data?.cards || [];
            const count = res?.data?.data?.count;
            setColumns((cols) => cols && cols.map((c) => {
                    if (String(c._id) !== String(column._id)) return c;
                    // Dedupe by pipeline_id: never re-append a startup that's already
                    // shown (guards against offset drift when cards are added/moved).
                    const seen = new Set(c.cards.map((x) => x.pipeline_id));
                    const fresh = more.filter((x) => !seen.has(x.pipeline_id));
                    const nextCards = [...c.cards, ...fresh];
                    // If nothing new arrived (empty page or all duplicates), cap the
                    // count so hasMore becomes false and we stop paging.
                    const nextCount = fresh.length === 0 ? nextCards.length : (count ?? c.count);
                    return { ...c, cards: nextCards, count: nextCount };
                })
            );
        } catch (e) {
            addToast('Could not load more startups.', 'error');
        } finally {
            inFlightRef.current.delete(column._id);
            setLoadingIds((s) => {
                const n = new Set(s);
                n.delete(column._id);
                return n;
            });
        }
    };

    // `notified` only proves the mail step did not throw - it stays true when
    // sendCustomizedEmails returns early. Two conditions must hold for a mail to actually
    // go: the target status is one it handles at all, and that status is not set to
    // "No Email" (unset still mails, off the default).
    const mailsOnMove = (statusValue) =>
        EMAILING_STATUSES.includes(statusValue) && customizeEmail?.[statusValue]?.mail_type !== 'noemail';

    // Runs before the library measures the board, so a group drag can hide the other selected
    // cards first. flushSync applies it now - the library measures right after this returns.
    // Dragging an unselected card drops the selection and moves that card alone.
    const handleBeforeCapture = (before) => {
        const column = findCardColumn(columns, before.draggableId);
        const card = column?.cards.find((x) => String(x.pipeline_id) === String(before.draggableId));
        const isSelected = !!card && isCardSelected(selection, card);
        const loaded = isSelected ? countLoadedSelected(columns, selection) : 1;
        // A selected column includes cards not loaded yet; those move via the Status action, not a drag.
        const blocked = isSelected && selectedCount(selection, columns) > loaded;
        flushSync(() => {
            if (!isSelected && hasSelection(selection)) onClearSelection?.();
            setDragInfo({ pipelineId: before.draggableId, count: blocked ? 1 : loaded, blocked });
        });
    };

    const handleDragStart = (start, provided) => {
        setIsDragging(true);
        const count = dragInfo?.count || 1;
        if (count > 1) provided.announce(`Dragging ${plural(count)}.`);
    };

    // Saves where cards were dropped (ranks between their new neighbours). Silent; if it
    // fails the board reloads so it never shows an order the server doesn't have.
    const savePosition = (column, index, ids) => placeCardsAsync({
        investor_status_id: column._id,
        pipeline_ids: ids,
        before_id: column.cards[index - 1]?.pipeline_id,
        after_id: column.cards[index + ids.length]?.pipeline_id,
    }).then((res) => (res?.data?.statusCode === 200 ? res.data.previous || {} : Promise.reject(res)))
        .catch(() => { loadBoard(); return null; });

    // The move toast with "Undo" for the length of the email hold.
    const showMoveToast = ({ text, willNotify, onUndo }) => {
        addToast(
            willNotify
                ? <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}><NotificationsOutlined sx={{ fontSize: 16 }} />{text}</Box>
                : text,
            'success',
            UNDO_MS,
            { label: 'Undo', onClick: onUndo },
            { hideIcon: willNotify, inlineAction: true },
        );
    };

    // Puts moved cards back (on screen first, then on the server) and cancels held emails.
    const undoMoves = async (undos, willNotify) => {
        const valid = undos.filter((u) => u.previous);
        if (!valid.length) return;
        setColumns((cols) => [...valid].sort((a, b) => a.index - b.index)
            .reduce((acc, u) => moveCardLocal(acc, u.pipeline_id, u.columnId, u.index), cols));
        onClearSelection?.();
        const results = await Promise.allSettled(valid.map((u) => undoMoveAsync(u)));
        const ok = results.filter((r) => r.status === 'fulfilled' && r.value?.data?.statusCode === 200);
        if (ok.length < valid.length) {
            addToast('Could not undo every move. Showing the latest board.', 'error');
            loadBoard();
            return;
        }
        const allCancelled = ok.every((r) => r.value?.data?.email_cancelled);
        addToast(
            !willNotify ? 'Move undone' : allCancelled ? 'Move undone · email cancelled' : 'Move undone · the email had already been sent',
            willNotify && !allCancelled ? 'warning' : 'success',
        );
        if (valid.length > 1) loadBoard(); // group: re-sync order with the server
    };

    // Applies a multi-drop and saves each card that changed column, reusing the same
    // per-card move the single drag and the bulk "Move to Status" use.
    const commitMultiDrop = async ({ draggableId, destination }) => {
        const plan = multiDrop(columns, draggableId, destination, (card) => isCardSelected(selection, card));
        if (!plan) return;
        const prev = columns;
        setColumns(plan.next);
        onClearSelection?.();
        const placing = savePosition(plan.to, plan.insertAt, plan.ids);
        if (!plan.moved.length) return; // regrouped inside one column - position only

        const results = await Promise.allSettled(plan.moved.map((p, i) => moveCardAsync({
            pipeline_id: p.card.pipeline_id,
            payload: { investor_status_id: plan.to._id, status: plan.to.value, display_order: plan.insertAt + i, defer_email: true },
        })));
        const prevRanks = (await placing) || {};
        const saved = results.filter((r) => r.status === 'fulfilled' && !(r.value?.data?.statusCode && r.value.data.statusCode !== 200));
        const total = plan.moved.length;

        if (!saved.length) {
            setColumns(prev); // rollback
            addToast('Could not move startups. Please try again.', 'error');
            return;
        }
        if (saved.length < total) {
            addToast(`Moved ${saved.length} of ${plural(total)}. The rest could not be moved.`, 'warning');
            loadBoard(); // show what the server actually holds
            return;
        }
        if (saved.some((r) => r.value?.data?.notified === false)) {
            addToast(`${plural(total)} moved to ${plan.to.label}. Some could not be notified.`, 'warning');
            return;
        }
        const willNotify = mailsOnMove(plan.to.value);
        const undos = plan.moved.map((p, i) => ({
            pipeline_id: p.card.pipeline_id,
            previous: results[i].value?.data?.previous && { ...results[i].value.data.previous, kanban_rank: prevRanks[p.card.pipeline_id] ?? null },
            email_job_id: results[i].value?.data?.email_job_id,
            columnId: p.from._id,
            index: p.index,
        }));
        showMoveToast({
            text: `${plural(total)} moved to ${plan.to.label}${willNotify ? ' · startups will be notified' : ''}`,
            willNotify,
            onUndo: () => undoMoves(undos, willNotify),
        });
    };

    const handleDragEnd = async (result, provided) => {
        setIsDragging(false);
        const lifted = dragInfo;
        setDragInfo(null);
        const { source, destination, draggableId } = result;
        if (!destination) return;
        if (lifted?.blocked) {
            addToast(`Use Status in the action bar to move all ${plural(selectedCount(selection, columns))} selected.`, 'warning');
            return;
        }
        if (lifted?.count > 1) {
            provided?.announce?.(`${plural(lifted.count)} dropped.`);
            commitMultiDrop(result); // no confirmation, same as a single drag (owner's call)
            return;
        }
        if (source.droppableId === destination.droppableId && source.index === destination.index) return;

        const prev = columns;
        const next = columns.map((c) => ({ ...c, cards: [...c.cards] }));
        const from = findColumn(next, source.droppableId);
        const to = findColumn(next, destination.droppableId);
        if (!from || !to) return;

        const [moved] = from.cards.splice(source.index, 1);
        if (!moved) return;
        to.cards.splice(destination.index, 0, { ...moved, investor_status_id: to._id });

        const crossColumn = source.droppableId !== destination.droppableId;
        if (crossColumn) {
            from.count = Math.max(0, (from.count || 1) - 1);
            to.count = (to.count || 0) + 1;
        }
        setColumns(next);
        // Like a group drop: a moved selected card leaves the selection, so bulk actions
        // never act on its old status.
        if (isCardSelected(selection, moved)) onClearSelection?.();

        // Save the new position; a status change (different column) is saved alongside it.
        const placing = savePosition(to, destination.index, [draggableId]);
        if (crossColumn) {
            try {
                const res = await moveCardAsync({
                    pipeline_id: draggableId,
                    payload: { investor_status_id: to._id, status: to.value, display_order: destination.index, defer_email: true },
                });
                const prevRanks = (await placing) || {};
                if (res?.data?.statusCode && res.data.statusCode !== 200) {
                    throw new Error(res?.data?.message);
                }
                if (res?.data?.notified === false) {
                    addToast('Startup could not be notified.', 'warning');
                    return;
                }
                const willNotify = mailsOnMove(to.value);
                const undo = { pipeline_id: draggableId, previous: res?.data?.previous && { ...res.data.previous, kanban_rank: prevRanks[draggableId] ?? null }, email_job_id: res?.data?.email_job_id };
                showMoveToast({
                    text: willNotify ? `Moved to ${to.label} · startup will be notified` : `Moved to ${to.label}`,
                    willNotify,
                    onUndo: () => undoMoves([{ ...undo, columnId: source.droppableId, index: source.index }], willNotify),
                });
            } catch (e) {
                setColumns(prev); // rollback
                loadBoard(); // the position may have saved even though the move didn't
                addToast(e?.response?.data?.message || 'Could not move startup. Please try again.', 'error');
            }
        }
    };

    const handleOpenReel = React.useCallback((item, isInvited) => {
        setSelectedReelItem(item);
        setSelectedReelViewType(isInvited ? 'deal-flow' : 'selected');
        setReelModalOpen(true);
    }, []);

    const handleCloseReel = React.useCallback(() => {
        setReelModalOpen(false);
        setSelectedReelItem(null);
    }, []);

    // A column's card total (filters applied), needed to tell "all" from "some".
    const totalOf = React.useCallback((statusId) => {
        const col = findColumn(columnsRef.current || [], statusId);
        return col ? (col.count ?? col.cards.length) : undefined;
    }, []);

    // Checkbox toggle; also remembers the card as the Shift+click start point.
    const handleToggleSelect = React.useCallback((card) => {
        anchorRef.current = card.pipeline_id;
        onSelectionChange?.((s) => toggleCard(s, card, totalOf(card.investor_status_id)));
    }, [onSelectionChange, totalOf]);

    // Cmd/Ctrl+click toggles one card; Shift+click selects the range from the last
    // clicked card within the same column. Reads columns via ref to stay stable for memo.
    const handleModifierSelect = React.useCallback((card, event) => {
        const column = findCardColumn(columnsRef.current, card.pipeline_id);
        if (event.shiftKey && column && anchorRef.current) {
            const ids = column.cards.map((x) => String(x.pipeline_id));
            const from = ids.indexOf(String(anchorRef.current));
            const to = ids.indexOf(String(card.pipeline_id));
            if (from > -1 && to > -1) {
                const range = column.cards.slice(Math.min(from, to), Math.max(from, to) + 1);
                onSelectionChange?.((s) => selectCards(s, range, totalOf(column._id)));
                return;
            }
        }
        anchorRef.current = card.pipeline_id;
        onSelectionChange?.((s) => toggleCard(s, card, totalOf(card.investor_status_id)));
    }, [onSelectionChange, totalOf]);

    // Column header checkbox: the whole column as state (like the list view's select-all) -
    // no fetch; cards loaded later show ticked. Unticks if the whole column is selected.
    const handleToggleColumn = (column) => {
        const total = column.count ?? (column.cards || []).length;
        anchorRef.current = null;
        if (columnCheck(selection[String(column._id)], total) === 'all') {
            onSelectionChange?.((s) => clearColumn(s, column._id));
        } else {
            onSelectionChange?.((s) => selectColumn(s, column._id));
        }
    };

    // Collapse or expand a column. Collapsing drops its cards from the selection, so bulk
    // actions never touch cards the investor can no longer see.
    const handleToggleCollapse = React.useCallback((column) => {
        setCollapsedIds((prev) => {
            const next = new Set(prev);
            const id = String(column._id);
            if (next.has(id)) next.delete(id);
            else {
                next.add(id);
                onSelectionChange?.((s) => clearColumn(s, id));
            }
            try { localStorage.setItem(collapseKey, JSON.stringify([...next])); } catch (e) { /* private mode */ }
            return next;
        });
    }, [collapseKey, onSelectionChange]);

    // Columns get identity-stable callbacks that always call the latest handler, so a
    // selection change does not hand every memoized column new props.
    const latestHandlers = React.useRef({});
    latestHandlers.current = { handleLoadMore, handleToggleColumn, onManageStatus, onInviteStartup };
    const stableHandlers = React.useMemo(() => ({
        onLoadMore: (column) => latestHandlers.current.handleLoadMore(column),
        onToggleColumn: (column) => latestHandlers.current.handleToggleColumn(column),
        onManageStatus: () => latestHandlers.current.onManageStatus?.(),
        onInviteStartup: () => latestHandlers.current.onInviteStartup?.(),
    }), []);

    // Esc clears the selection, unless a drag, dialog or menu is using the key.
    const anySelected = hasSelection(selection);
    React.useEffect(() => {
        if (!anySelected) return undefined;
        const onKeyDown = (e) => {
            if (e.key !== 'Escape' || isDragging || isOverlayOpen()) return;
            onClearSelection?.();
        };
        window.addEventListener('keydown', onKeyDown);
        return () => window.removeEventListener('keydown', onKeyDown);
    }, [anySelected, isDragging, onClearSelection]);

    // Skeleton only when there is no board to show; an in-place refresh keeps the board.
    if (columns === null) {
        // Prefer the cached layout for this tab (has real per-column counts); fall back
        // to the investor's status list (labels, no counts); finally generic columns.
        const cached = readStruct(pipelineId);
        const skeletonCols = (cached && cached.length)
            ? cached
            : (statuses?.length
                ? statuses.map((s) => ({ _id: s.value || s._id, label: s.label || s.status, count: 0 }))
                : [0, 1, 2, 3].map((i) => ({ _id: i, label: '', count: 0 })));
        return (
            <Box sx={{ width: '100%' }}>
                <Box sx={{ display: 'flex', gap: '1rem', overflow: 'hidden', pt: '0.5rem' }}>
                    {skeletonCols.map((c) => (
                        <ColumnSkeleton key={c._id} label={c.label} cardCount={c.count} reels={c.reels} collapsed={collapsedIds.has(String(c._id))} />
                    ))}
                </Box>
            </Box>
        );
    }

    if (!columns.length) {
        return (
            <Box sx={{ width: '100%', textAlign: 'center', py: '4rem', color: '#667085' }}>
                <Typography>No pipeline statuses configured yet.</Typography>
            </Box>
        );
    }

    return (
        <Box sx={{ width: '100%' }}>
            <DragDropContext onBeforeCapture={handleBeforeCapture} onDragStart={handleDragStart} onDragEnd={handleDragEnd}>
                {/* Horizontal scroller only; each column scrolls its own card list.
                    alignItems: stretch gives every column an invisible full-height shell -
                    the *visible* container inside it is still only as tall as its cards
                    (see KanbanColumn). That shell is the droppable, so releasing a card in
                    the empty space below a short container still lands it in that column
                    instead of the last one hovered.
                    height (not maxHeight) keeps the horizontal scrollbar pinned to the
                    bottom rather than floating up when the columns are short. */}
                <Box
                    onMouseMove={(e) => {
                        const rect = e.currentTarget.getBoundingClientRect();
                        const near = rect.bottom - e.clientY <= H_SCROLL_ZONE;
                        if (near !== showHScroll) setShowHScroll(near);
                    }}
                    onMouseLeave={() => setShowHScroll(false)}
                    sx={{
                    display: 'flex', gap: '1rem', overflowX: 'auto', overflowY: 'hidden',
                    // 14rem: page header + the shared List/Board header bar inside the card.
                    height: 'calc(100vh - 14rem)', alignItems: 'stretch', pt: '0.5rem', pb: '0.5rem',
                    // Horizontal bar appears only when the pointer nears the bottom edge.
                    scrollbarWidth: 'thin',
                    scrollbarColor: showHScroll ? '#9e9e9e transparent' : 'transparent transparent',
                    '&::-webkit-scrollbar': { height: '0.313rem' },
                    '&::-webkit-scrollbar-thumb': { backgroundColor: showHScroll ? '#9e9e9e' : 'transparent', borderRadius: '6px' },
                    '&::-webkit-scrollbar-track': { backgroundColor: 'transparent' },
                }}>
                    {styledColumns.map((col) => (
                        <KanbanColumn
                            key={col._id}
                            column={col}
                            reelStatuses={reelStatuses}
                            onLoadMore={stableHandlers.onLoadMore}
                            isLoadingMore={loadingIds.has(col._id)}
                            isDragging={isDragging}
                            dragInfo={dragInfo}
                            selection={selection[String(col._id)]}
                            anySelected={anySelected}
                            onToggleSelect={handleToggleSelect}
                            onModifierSelect={handleModifierSelect}
                            onToggleColumn={stableHandlers.onToggleColumn}
                            collapsed={collapsedIds.has(String(col._id))}
                            onToggleCollapse={handleToggleCollapse}
                            onOpenMenu={onOpenCardMenu}
                            onCardAction={onCardAction}
                            onOpenReel={handleOpenReel}
                            onManageStatus={onManageStatus ? stableHandlers.onManageStatus : undefined}
                            onInviteStartup={onInviteStartup ? stableHandlers.onInviteStartup : undefined}
                        />
                    ))}
                    {onAddStatus && (
                        <Box
                            onClick={onAddStatus}
                            sx={{
                                // alignSelf keeps this pinned to the top now that the row
                                // stretches its children to full height. Height matches a column's
                                // purple header band (py 1.15rem x2 + the 24px count chip), so
                                // "Add Status" sits level with the status headers.
                                width: { xs: 200, sm: 220 }, flexShrink: 0, height: 'calc(2.3rem + 24px)', alignSelf: 'flex-start',
                                border: `1.5px dashed ${theme?.palette?.customColors?.g75 || '#C7CAD1'}`,
                                borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center',
                                gap: '0.375rem', color: indigo, fontWeight: 600, fontSize: '0.9rem', cursor: 'pointer',
                                backgroundColor: 'transparent', transition: 'background-color 0.15s ease, border-color 0.15s ease',
                                '&:hover': { backgroundColor: '#F5F6FC', borderColor: indigo },
                            }}
                        >
                            <Add sx={{ fontSize: 20 }} /> Add Status
                        </Box>
                    )}
                </Box>
            </DragDropContext>

            {/* Reel Modal Overlay */}
            <ReelModal
                open={reelModalOpen}
                onClose={handleCloseReel}
                item={selectedReelItem}
                investor_id={investor_id}
                viewType={selectedReelViewType}
                userPermissions={userPermissions}
                addToast={addToast}
            />
        </Box>
    );
};

export default KanbanBoard;
